import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { setSession } from "../../auth/session.js";
import { AuthLayout, Button, ErrorNotice, Field, Input } from "../../components/form.js";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { token } = useSearch({ from: "/reset-password" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm.length > 0 && confirm !== password;

  const reset = useMutation({
    mutationFn: () => api.auth.password.reset(token, password),
    onSuccess: () => {
      // Every session was ended server-side, this browser's included.
      setSession(queryClient, null);
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!mismatch) reset.mutate();
  };

  if (!token) {
    return (
      <AuthLayout title={t("auth.reset.invalidTitle")} subtitle={t("auth.reset.invalidSubtitle")}>
        <Link
          to="/forgot-password"
          className="text-sm text-accent underline-offset-2 hover:underline"
        >
          {t("auth.reset.requestAgain")}
        </Link>
      </AuthLayout>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthLayout title={t("auth.reset.doneTitle")} subtitle={t("auth.reset.doneSubtitle")}>
        <Link to="/login" className="text-sm text-accent underline-offset-2 hover:underline">
          {t("auth.signup.signIn")}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.reset.title")} subtitle={t("auth.reset.subtitle")}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t("auth.reset.newPassword")} hint={t("auth.fields.passwordHint")}>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field
          label={t("auth.reset.confirmPassword")}
          {...(mismatch ? { hint: t("auth.reset.mismatch") } : {})}
        >
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        <ErrorNotice error={reset.error} />
        <Button type="submit" disabled={reset.isPending || mismatch}>
          {reset.isPending ? t("common.loading") : t("auth.reset.submit")}
        </Button>
        <p className="text-center text-sm text-ink-2">
          <Link to="/forgot-password" className="text-accent underline-offset-2 hover:underline">
            {t("auth.reset.requestAgain")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
